import type {
  Department,
  DepartmentResponse,
  GetDepartmentListParams,
  GetDepartmentListResponse,
} from '@/types';
import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';

export async function getDepartmentList(
  companyId: string,
  params: GetDepartmentListParams
) {
  const { data } = await api.get<GetDepartmentListResponse>(
    `/companies/${companyId}/departments`,
    {
      params,
    }
  );
  return data;
}

export async function getDepartmentById(
  companyId: string,
  departmentId: string
) {
  const { data } = await api.get<DepartmentResponse>(
    `/companies/${companyId}/departments/${departmentId}`
  );
  return data;
}

/**
 * Create a new department in the company
 *
 * @param {string} companyId - The id of the company
 * @param {Partial<Department>} data - The department data to be created
 * @returns {Promise<Department>} - The created department
 */
export async function createDepartment(
  companyId: string,
  data: Partial<Department>
) {
  const res = await api.post<ApiResponse<Department>>(
    `/companies/${companyId}/departments`,
    data
  );
  return res.data;
}

/**
 * Update an existing department in the company
 *
 * @param {string} companyId - The id of the company
 * @param {string} departmentId - The id of the department to be updated
 * @param {Partial<Department>} data - The department data to be updated
 * @returns {Promise<Department>} - The updated department
 */
export async function updateDepartment(
  companyId: string,
  departmentId: string,
  data: Partial<Department>
) {
  const res = await api.put<ApiResponse<Department>>(
    `/companies/${companyId}/departments/${departmentId}`,
    data
  );
  return res.data;
}

/**
 * Update the status of a department
 *
 * @param {string} companyId - The id of the company
 * @param {string} departmentId - The id of the department to be updated
 */
export async function updateDepartmentStatus(
  companyId: string,
  departmentId: string
) {
  const res = await api.patch(
    `/companies/${companyId}/departments/${departmentId}/status`
  );
  return res.data;
}

/**
 * Delete an existing department in the company
 *
 * @param {string} companyId - The id of the company
 * @param {string} departmentId - The id of the department to be deleted
 *
 * @note This API is yet to be implemented from the backend
 */
export async function deleteDepartment(
  companyId: string,
  departmentId: string
) {
  const res = await api.delete(
    `/companies/${companyId}/departments/${departmentId}`
  );
  return res.data;
}
